import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import closeIcon from "svg/closeWhiteFill.svg";
import editIcon from "svg/pencil.svg";
import floorDisplayImage from "svg/floorDisplayImage.svg";
import { currencyValue } from "utils/common";
import Modal from "common/modal";
import Button from "react-bootstrap/Button";
import InputField from "common/customInputField";
import QuoteSummaryFields from "../common/quoteSummaryFields";
import { toast } from "react-toastify";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "store/slices/api";
import { sortBy } from "lodash";

const QuoteSummary = ({ territoryAccess }) => {
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const { sub, fcRole, hasUpdateAccess } = useSelector((state) => state.loggedInUser);
  const [showFloorPlanModal, setShowFloorPlanModal] = useState(false);
  const [showDeleteFloorModal, setShowDeleteFloorModal] = useState(false);
  const [showEditFloorNameModal, setShowEditFloorNameModal] = useState(false);
  const [showDuplicateFloorModal, setShowDuplicateFloorModal] = useState(false);
  const [duplicateFloorData, setDuplicateFloorData] = useState({ floorId: "", floorName: "" });
  const initialvalues = { floorId: "", index: "", floorName: "" };
  const [removeFloorData, setRemoveFloorData] = useState(initialvalues);
  const [editFloorNameData, setEditFloorNameData] = useState({ floorId: "", floorName: "" });
  const [floorNameInput, setFloorNameInput] = useState("");

  const [updateFloor] = api.useUpdatePriceQuoteFloorMutation();
  const [addFloor] = api.useAddPriceQuoteFloorMutation();
  const [deleteFloor] = api.useDeletePriceQuoteFloorMutation();
  const [duplicateFloor] = api.useDuplicatePriceQuoteFloorMutation();

  const { data: quoteFields = {} } = api.useGetPriceQuoteQuery(quoteId);
  const { data: floorPlans = [], isLoading: floorPlansLoading } = api.useGetPriceQuoteFloorsQuery(quoteId);

  const [checkIfUserCanUpdatePrc] = api.useLazyGetCanUserUpdatePrcQuery();

  // quotes created from sites do not have ranks set. TODO: fix quote creation from site so that it gives floors ranks
  const sortedFloorPlans = sortBy(floorPlans, ["rank", "floorName"]).map((floor, index) => ({
    ...floor,
    rank: index + 1,
  }));

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 300, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const DraggableItem = ({ data, index }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: data.rank });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: territoryAccess ? (isDragging ? "grab" : "pointer") : "default",
    };

    return (
      <div
        className="floorPlan_container"
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...(territoryAccess ? listeners : {})}
      >
        {territoryAccess && (
          <Button
            className="floorPlan_container__remove"
            onClick={() => handleRemoveFloor(data.floorId, index, data.floorName)}
          >
            <img
              src={closeIcon}
              alt="delete"
              width={10}
              height={10}
              style={{ position: "relative", top: "-4px", left: "-3px" }}
            />
          </Button>
        )}
        <div className="d-flex p-2 flex-wrap">
          <div className="d-flex flex-column info-item me-4" style={{ width: "14rem", maxWidth: "100%" }}>
            <div className="info-item__label">Floor Plan</div>
            <div className="info-item__floor-value">
              {data.floorName}
              {territoryAccess && (
                <img
                  src={editIcon}
                  className="editIcon"
                  alt="edit"
                  onPointerDown={() => handleEditFloorName(data.floorId, data.floorName)}
                />
              )}
            </div>
          </div>
          <div className="d-flex flex-grow-1 align-items-start flex-wrap">
            <div className="d-flex flex-column info-item me-4" style={{ minWidth: "5.75rem" }}>
              <div className="info-item__label">Material</div>
              <div className="info-item__value">
                {/* TODO: eventually restore this alert when a good products endpoint exists */}
                {/* {floorIds.includes(data.floorId) && (
                  <CustomTooltip
                    placement="top"
                    trigger="hover"
                    text="One or more products in this floor plan have a different price on another floor plan."
                  >
                    <img className="alert_style_icon" src={alertIcon} />
                  </CustomTooltip>
                )} */}
                {currencyValue(data.material)}
              </div>
            </div>
            <div className="d-flex flex-column info-item me-4" style={{ minWidth: "5.75rem" }}>
              <div className="info-item__label">Labor</div>
              <div className="info-item__value">{currencyValue(data.labor)}</div>
            </div>
            <div className="d-flex flex-column info-item me-4" style={{ minWidth: "5.75rem" }}>
              <div className="info-item__label">Subtotal</div>
              <div className="info-item__value">{currencyValue(data.subTotal)}</div>
            </div>
            <div className="d-flex flex-column info-item me-4" style={{ minWidth: "5.75rem" }}>
              <div className="info-item__label">Tax</div>
              <div className="info-item__value">{currencyValue(data.salesTax)}</div>
            </div>
            <div className="d-flex flex-column info-item me-4" style={{ minWidth: "5.75rem" }}>
              <div className="info-item__label text-nowrap">Total with Tax</div>
              <div className="info-item__value">{currencyValue(data.total)}</div>
            </div>
          </div>
          <div className="d-flex flex-wrap">
            {territoryAccess && (
              <a className="info-item-link" onPointerDown={() => handleDuplicateFloor(data.floorId, data.floorName)}>
                Duplicate
              </a>
            )}
            <a className="info-item-link" onPointerDown={() => handleViewDetails(data.floorId, quoteId)}>
              {territoryAccess ? "View/Edit" : "View"}
            </a>
          </div>
        </div>
      </div>
    );
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const oldIndex = sortedFloorPlans.findIndex((item) => item.rank === active.id);
    const newIndex = sortedFloorPlans.findIndex((item) => item.rank === over.id);
    if (oldIndex !== newIndex) {
      arrayMove(sortedFloorPlans, oldIndex, newIndex).forEach((floor, index) => {
        updateFloor({ quoteId, floorId: floor.floorId, rank: index + 1 });
      });
    }
  };

  const handleAddFloorPlan = () => {
    setShowFloorPlanModal(true);
    setFloorNameInput("");
  };

  const handleDuplicateNameChange = (e) => {
    const value = e.target.value;
    setDuplicateFloorData({ ...duplicateFloorData, floorName: value });
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setFloorNameInput(value);
  };

  const handleEditFloor = (e) => {
    const value = e.target.value;
    setEditFloorNameData({ ...editFloorNameData, floorName: value });
  };

  const handleCancel = () => {
    setShowFloorPlanModal(false);
    setShowDeleteFloorModal(false);
    setShowEditFloorNameModal(false);
    setShowDuplicateFloorModal(false);
  };

  const handleRemoveFloor = (floorId, index, floorName) => {
    setRemoveFloorData({ floorId: floorId, index: index, floorName: floorName });
    setShowDeleteFloorModal(true);
  };

  const handleViewDetails = (floorId, quoteId) => {
    navigate(`/priceQuote/${quoteId}/${floorId}`);
  };

  const handleSave = () => {
    if (showFloorPlanModal) {
      setShowFloorPlanModal(false);
      addFloor({ floorName: floorNameInput, quoteId, rank: sortedFloorPlans.length + 1 });
    } else if (showDeleteFloorModal) {
      deleteFloor(removeFloorData.floorId);
      setShowDeleteFloorModal(false);
    } else if (showEditFloorNameModal) {
      setShowEditFloorNameModal(false);
      toast.promise(updateFloor({ quoteId, ...editFloorNameData }).unwrap(), {
        pending: "Saving floor plan name",
        success: "Floor plan name saved successfully",
        error: "Failed to save floor plan name",
      });
    } else if (showDuplicateFloorModal) {
      setShowDuplicateFloorModal(false);
      duplicateFloor({
        floorName: duplicateFloorData.floorName,
        floorId: duplicateFloorData.floorId,
        createdBy: sub,
        rank: sortedFloorPlans.length + 1,
      });
    }
  };

  const handleNavigatePrc = async () => {
    const body = {
      userRole: fcRole,
      acctNbr: quoteFields?.accountNumber,
      terrCcn: quoteFields.territory,
      storeCcn: quoteFields.storeNumber,
    };
    const canUserUpdatePrc = (await checkIfUserCanUpdatePrc(body)).data;
    if (canUserUpdatePrc === "FALSE" || canUserUpdatePrc === "DEPT_30_ONLY") {
      toast.error(
        "You cannot update the PRC for this account because your home store and territory do not match those on the account.",
      );
    } else {
      navigate(`/priceQuote/${quoteId}/updatePrc`);
    }
  };

  const handleEditFloorName = (floorId, floorName) => {
    setShowEditFloorNameModal(true);
    setEditFloorNameData({ floorId, floorName });
  };

  const handleDuplicateFloor = (floorId, floorName) => {
    setShowDuplicateFloorModal(true);
    setDuplicateFloorData({ floorId: floorId, floorName: floorName });
  };

  const editFloorNameModal = () => {
    return (
      <form className="search-form-container__fields__form">
        <div className="row">
          <div className="col-12 col-sm-12 col-md-12 col-lg-6 search-form-container__fields__form-group">
            <InputField
              type="text"
              label="Floor Plan"
              id="floorPlanName"
              placeholder="Enter a name"
              fieldClasses="form-control search-form-container__fields__form-group__item"
              value={editFloorNameData.floorName}
              onChange={handleEditFloor}
              disabled={false}
              isRequired={false}
            />
          </div>
        </div>
      </form>
    );
  };

  const addFloorPlanModal = () => {
    return (
      <form className="search-form-container__fields__form">
        <div className="row">
          <div className="col-12 col-sm-12 col-md-12 col-lg-6 search-form-container__fields__form-group">
            <InputField
              type="text"
              label="Floor Plan"
              id="floorName"
              placeholder="Enter a name"
              fieldClasses="form-control search-form-container__fields__form-group__item"
              value={floorNameInput}
              onChange={handleChange}
              disabled={false}
              isRequired={false}
            />
          </div>
        </div>
      </form>
    );
  };

  const duplicateFloorModal = () => {
    return (
      <form className="search-form-container__fields__form">
        <div className="row">
          <div className="col-12 col-sm-12 col-md-12 col-lg-6 search-form-container__fields__form-group">
            <InputField
              type="text"
              label="Floor Plan"
              id="floorName"
              placeholder="Enter a name"
              fieldClasses="form-control search-form-container__fields__form-group__item"
              value={duplicateFloorData.floorName}
              onChange={handleDuplicateNameChange}
              disabled={false}
              isRequired={false}
            />
          </div>
        </div>
      </form>
    );
  };

  let saveButtonText = "";
  let title = "";
  if (showFloorPlanModal) {
    title = "Add Floor Plan Name";
    saveButtonText = "Save";
  } else if (showDeleteFloorModal) {
    title = "Delete Floor Plan";
    saveButtonText = "Remove";
  } else if (showEditFloorNameModal) {
    title = "Edit Floor Plan Name";
    saveButtonText = "Save";
  } else if (showDuplicateFloorModal) {
    title = "Duplicate Floor Plan";
    saveButtonText = "Confirm";
  }

  return (
    <div className="customer-details">
      <QuoteSummaryFields />
      {floorPlansLoading === false && (
        <>
          {sortedFloorPlans.length == 0 && (
            <div className="d-flex justify-content-center">
              <img src={floorDisplayImage} alt="floorDisplayImage" className="floor_img" />
            </div>
          )}
          {territoryAccess && quoteFields.punchoutPublished !== true && (
            <div className={`d-flex mt-5 justify-content-${sortedFloorPlans.length ? "between" : "center"} `}>
              <Button variant="primary" onClick={handleAddFloorPlan}>
                Add Floor Plan
              </Button>
              {sortedFloorPlans.length !== 0 && (
                <div>
                  {hasUpdateAccess && (
                    <Button
                      variant="outline-primary"
                      onClick={() => navigate(`/priceQuote/${quoteId}/updateLaborPricing`)}
                    >
                      Update Labor Pricing
                    </Button>
                  )}
                  {(fcRole === "FcStoreManager" || fcRole === "FcOpsMgr" || fcRole === "FcSalesRep") && (
                    <Button className="m-0" variant="outline-primary" onClick={handleNavigatePrc}>
                      Update PRC
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {sortedFloorPlans.length > 0 && (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
          <SortableContext items={sortedFloorPlans.map((floor) => floor.rank)} strategy={verticalListSortingStrategy}>
            {sortedFloorPlans.map((floor, index) => (
              <DraggableItem key={floor.rank} data={floor} index={index} />
            ))}
          </SortableContext>
        </DndContext>
      )}

      <div className="d-flex footer_display justify-content-between">
        <Button variant="outline-primary" onClick={() => navigate(`/priceQuote/${quoteId}#quote-details`)}>
          Back
        </Button>
        <Button className="m-0" variant="primary" onClick={() => navigate(`/priceQuote/${quoteId}#pdf-options`)}>
          Next
        </Button>
      </div>

      {(showFloorPlanModal || showDeleteFloorModal || showEditFloorNameModal || showDuplicateFloorModal) && (
        <Modal
          title={title}
          saveButtonText={saveButtonText}
          closeButtonText={"Cancel"}
          handleSave={handleSave}
          handleCancel={handleCancel}
          saveButtonClass={title === "Delete Floor Plan" && "removeBtnClass"}
          size="lg"
        >
          {title === "Add Floor Plan Name" ? (
            addFloorPlanModal()
          ) : title === "Edit Floor Plan Name" ? (
            editFloorNameModal()
          ) : showDuplicateFloorModal ? (
            duplicateFloorModal()
          ) : (
            <p className="confirmation-text">
              Are you sure you want to remove <strong>{removeFloorData.floorName}</strong> ?
            </p>
          )}
        </Modal>
      )}
    </div>
  );
};

export default QuoteSummary;
